// Find what a contact is missing -- an address, or the name a sequence needs.
//
// Three contacts want this and they are not the same case:
//
// * One has a LinkedIn URL and no address. That is an exact lookup -- a profile URL names one
//   person -- and the answer can be trusted about as far as Amplemarket's data goes.
// * One has neither. The lookup is a guess from a name and an employer, which is the weakest of
//   the three matching signals Amplemarket offers.
// * One has an address and no name. That is an exact lookup on the address we already hold, and
//   it is the row that reads as pushable and is then refused by most sequences it could go
//   into: 45 of the 80 sampled reference `first_name`, and the server omits an empty one rather
//   than sending `Hi ,`.
//
// Because of that second case, **nothing is ever accepted silently.** The server finds a
// candidate and holds it; this shows who it found -- name, title, company, address -- and a
// human presses Use. A wrong match here does not just store a bad row, it puts a stranger into
// a live email sequence under a colleague's name.
//
// The browser never sends an address or a name in either direction that matters. It names an
// account and a contact; the server does the lookup, holds the result against the contact id,
// and resolves it again at push time. What was found appears here only so the person deciding
// can see what they are deciding.

import type { Contact, EnrichCandidate } from '../api/types';
import { enrichLookupKey } from './permissions';

export interface EnrichState {
  contactId: string;
  status: 'loading' | 'found' | 'missing';
  candidate: EnrichCandidate | null;
  message: string;
}

// What the candidate says about itself, so the person confirming can see the match rather than
// a yes/no. Company is the field that actually catches a wrong match.
function Candidate({
  candidate,
  onAccept,
  onDiscard,
}: {
  candidate: EnrichCandidate;
  onAccept: () => void;
  onDiscard: () => void;
}) {
  const facts = [candidate.title, candidate.company_name || candidate.company_domain]
    .filter(Boolean);
  // A candidate with no address is not a half-miss: the name lookup does not ask for one,
  // because the contact already has one. The server reports a genuinely addressless result for
  // a contact with no address as a miss, so a card without an address is always the name case.
  const acceptsAnAddress = Boolean(candidate.email);

  return (
    <div className="enrichfound">
      <div className="enrichwho">
        <b>{candidate.name || 'Unnamed match'}</b>
        {facts.length ? ` — ${facts.join(' · ')}` : ''}
      </div>
      <div className="enrichmail">
        {acceptsAnAddress ? candidate.email : 'the address on this contact is unchanged'}
      </div>
      {candidate.linkedin_url ? (
        <a className="linkchip" href={candidate.linkedin_url} target="_blank" rel="noopener">
          <span className="ic">in</span>Profile
        </a>
      ) : null}
      <div className="enrichnote">
        {candidate.source === 'workspace'
          ? 'Already in the Amplemarket workspace.'
          : 'Matched in the Amplemarket database.'}
      </div>
      {candidate.company_matches === false ? (
        <div className="enrichwarn">
          This is not {candidate.account_name || 'the account'}. Usually that means they have
          changed jobs — which is worth knowing before you send — but it can also be the wrong
          person. Check the profile.
        </div>
      ) : null}
      <div className="enrichacts">
        <button className="btn primary" type="button" onClick={onAccept}>
          {acceptsAnAddress ? 'Use this address' : 'Use this name'}
        </button>
        <button className="btn" type="button" onClick={onDiscard}>Discard</button>
      </div>
    </div>
  );
}

// What the button says, and what it says the lookup will be keyed on. Both come off the same
// `enrichLookupKey` the server mirrors, so the label and the promise cannot drift apart.
//
// The key is worth saying before the click rather than after. A LinkedIn URL and an address
// each identify one person exactly; a name and a company does not, and that is the case where
// the result is worth reading carefully rather than accepting on sight.
function lookupPrompt(contact: Contact): { label: string; searchedBy: string } {
  const key = enrichLookupKey(contact);
  if (key === 'email') {
    return { label: 'Find name', searchedBy: `by ${contact.email}` };
  }
  if (key === 'linkedin') {
    return { label: 'Find email', searchedBy: 'by their LinkedIn profile' };
  }
  return { label: 'Find contact details', searchedBy: 'by their name and this company' };
}

// The control that replaces the blocked state on a contact with no address, and sits beside the
// push checkbox on a contact that has an address and no name. The push module stops rendering
// its "No push" chip for the first of those, so this sits in its place rather than beside it --
// two controls arguing about the same row reads worse than either alone.
export function EnrichControl({
  contact,
  state,
  onFind,
  onAccept,
  onDismiss,
}: {
  contact: Contact;
  state: EnrichState | null;
  onFind: () => void;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const active = state?.contactId === contact.id;

  if (!active) {
    const prompt = lookupPrompt(contact);
    return (
      <span className="enrichoffer">
        <button className="pushchip" type="button" onClick={onFind}>{prompt.label}</button>
        <span className="pushnote">Amplemarket, {prompt.searchedBy}</span>
      </span>
    );
  }

  if (state.status === 'loading') {
    return <span className="pushnote">Looking this contact up in Amplemarket…</span>;
  }

  if (state.status === 'found' && state.candidate) {
    return <Candidate candidate={state.candidate} onAccept={onAccept} onDiscard={onDismiss} />;
  }

  // A miss is the expected answer for these people -- they are the ones Salesforce and an
  // earlier enrichment pass already failed on -- so it reads as an answer, not as a fault.
  return (
    <div className="enrichmiss">
      <span className="pushnote">{state.message}</span>
      <button className="rowbtn" type="button" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}
